import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/EditOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import PercentIcon from '@mui/icons-material/Percent'
import type { Tax } from '@iobiz/shared'
import { api } from '../api/client.js'
import { useSessionStore } from '../stores/sessionStore.js'
import { EmptyState } from '../ui/DataTable.js'
import { ActionsDOnglet } from '../ui/ActionsDOnglet.js'
import { runCycle } from '../data/sync/engine.js'

/**
 * Taux de TVA de la boutique.
 *
 * ⭐ Dans le CATALOGUE, pas dans les réglages : c'est en saisissant un article
 * qu'on s'aperçoit qu'il manque un taux, et un aller-retour vers un écran de
 * configuration éloigné est précisément ce qui fait renoncer.
 *
 * ⚠️ Écran serveur, contrairement au reste du catalogue : les taux changent
 * une ou deux fois par an, pas au comptoir. Ils sont en revanche RÉPLIQUÉS
 * vers les appareils, sans quoi la caisse hors ligne calculerait une TVA nulle.
 */
export function TaxListPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const store = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))
  const storeId = store?.storeId

  const [taxes, setTaxes] = useState<Tax[]>([])
  const [enCours, setEnCours] = useState<Tax | null>(null)
  const [ouvert, setOuvert] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const [nom, setNom] = useState('')
  const [taux, setTaux] = useState('')
  const [parDefaut, setParDefaut] = useState(false)
  const [actif, setActif] = useState(true)

  const charger = useCallback(async () => {
    if (!storeId) return
    try {
      const r = await api.get<{ data: Tax[] }>(`/stores/${storeId}/taxes`)
      setTaxes(r.data)
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Lecture impossible.')
    }
  }, [storeId])

  useEffect(() => {
    void charger()
  }, [charger])

  function ouvrir(t: Tax | null): void {
    setEnCours(t)
    setNom(t?.name ?? '')
    setTaux(t ? String(t.rate) : '')
    setParDefaut(t?.isDefault ?? false)
    setActif(t?.isActive ?? true)
    setOuvert(true)
  }

  async function enregistrer(): Promise<void> {
    if (!storeId) return
    const corps = {
      name: nom,
      rate: Number(taux.replace(',', '.')) || 0,
      isDefault: parDefaut,
      isActive: actif,
    }
    try {
      /**
       * ⚠️ SANS l'identifiant d'appareil. Un taux est écrit côté SERVEUR, pas
       * dans la réplique : attribué à cet appareil, le filtre anti-écho
       * refuserait ensuite de le lui renvoyer. Le taux existerait en base et
       * n'apparaîtrait JAMAIS dans le formulaire d'article de celui qui vient
       * de le créer — quatrième occurrence de ce piège, après les deux imports
       * et le rattrapage de codes-barres.
       */
      if (enCours) await api.put(`/stores/${storeId}/taxes/${enCours.id}`, corps, { sansAppareil: true })
      else await api.post(`/stores/${storeId}/taxes`, corps, { sansAppareil: true })
      setOuvert(false)
      await charger()
      // Les appareils doivent recevoir le nouveau taux : sans cycle, la caisse
      // continuerait de calculer avec l'ancien jusqu'au battement suivant.
      await runCycle(storeId)
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Enregistrement impossible.')
    }
  }

  async function supprimer(t: Tax): Promise<void> {
    if (!storeId) return
    try {
      await api.delete(`/stores/${storeId}/taxes/${t.id}`, { sansAppareil: true })
      await charger()
      await runCycle(storeId)
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Suppression impossible.')
    }
  }

  return (
    <>
      {/* ⭐ L'action rejoint la barre d'onglets, comme au catalogue : le titre
          « Taux de TVA » ne faisait que répéter l'onglet actif, et sa ligne
          repoussait le tableau d'autant. */}
      <ActionsDOnglet>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => ouvrir(null)}>
          Nouveau taux
        </Button>
      </ActionsDOnglet>

      {message && (
        <Alert severity="warning" sx={{ mb: 4 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      {taxes.length === 0 ? (
        <EmptyState
          icon={<PercentIcon />}
          title="Aucun taux de TVA"
          description="Créez les taux en vigueur dans votre pays. Ils seront proposés sur chaque article et appliqués aux ventes."
          action={
            <Button variant="contained" onClick={() => ouvrir(null)}>
              Nouveau taux
            </Button>
          }
        />
      ) : (
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Libellé</TableCell>
                  <TableCell align="right">Taux</TableCell>
                  <TableCell>État</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {taxes.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>{t.name}</TableCell>
                    <TableCell align="right" className="tabular">
                      {t.rate} %
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        {t.isDefault && <Chip label="par défaut" size="small" color="primary" />}
                        {!t.isActive && <Chip label="inactif" size="small" />}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" aria-label="Modifier" onClick={() => ouvrir(t)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Supprimer"
                        onClick={() => void supprimer(t)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      <Dialog open={ouvert} onClose={() => setOuvert(false)} fullWidth maxWidth="xs">
        <DialogTitle>{enCours ? 'Modifier le taux' : 'Nouveau taux'}</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: 4 }}>
          <TextField
            label="Libellé"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            placeholder="TVA 19,25 %"
            autoFocus
            fullWidth
          />
          <TextField
            label="Taux"
            value={taux}
            onChange={(e) => setTaux(e.target.value.replace(/[^\d.,]/g, ''))}
            InputProps={{ endAdornment: '%' }}
            inputProps={{ inputMode: 'decimal' }}
            fullWidth
          />
          {/* ⚠️ L'explication vit ICI, à côté de l'interrupteur qui la déclenche.
              Elle tenait en légende d'un titre de page, à trois écrans de la
              décision qu'elle éclaire. */}
          <Box>
            <FormControlLabel
              control={
                <Switch checked={parDefaut} onChange={(e) => setParDefaut(e.target.checked)} />
              }
              label="Taux par défaut"
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Proposé automatiquement à la création d’un article.
            </Typography>
          </Box>
          <FormControlLabel
            control={<Switch checked={actif} onChange={(e) => setActif(e.target.checked)} />}
            label="Actif"
          />
        </DialogContent>
        <DialogActions sx={{ px: 6, py: 4 }}>
          <Button onClick={() => setOuvert(false)}>Annuler</Button>
          <Button variant="contained" disabled={!nom.trim()} onClick={() => void enregistrer()}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
